import { preloadImage } from '@mail/3promo';
import managePromoImgDark from 'img/safe/safePromo2Dark.png';
import managePromoImg from 'img/safe/safePromo2Light.png';
import safePromoCleanerCloudMp4 from 'img/safe/safePromoCleanerCloud_light.mp4';
import safePromoCleanerCloudWebm from 'img/safe/safePromoCleanerCloud_light.webm';
import safePromoCleanerMailMp4 from 'img/safe/safePromoCleanerMail_light.mp4';
import safePromoCleanerMailWebm from 'img/safe/safePromoCleanerMail_light.webm';
import promoImgDark from 'img/safe/safePromoDark.png';
import promoImg from 'img/safe/safePromoLight.png';
import { IS_PAID_USER } from 'reactApp/appHelpers/configHelpers';
import type { UserQuotaGroupId } from 'reactApp/types/QuotaCleaner';
import { sendDwh } from 'reactApp/utils/ga';
import { getPlatformInfo } from 'reactApp/utils/gaHelpers';
import { isDarkThemeModeEnabled } from 'reactApp/utils/theme';

const IMAGE_PRELOAD_TIMEOUT = 3 * 1000;

export const enum SafePromoType {
    base = 'base',
    afterMoveTrashbinFiles = 'afterMoveTrashbinFiles',
    afterMoveCleanerFiles = 'afterMoveCleanerFiles',
    afterMoveCleanerLetters = 'afterMoveCleanerLetters',
    moreAboutSafe = 'moreAboutSafe',
    manage = 'manage',
}

const AnalyticSafePromoTypeName = {
    [SafePromoType.base]: 'base',
    [SafePromoType.afterMoveTrashbinFiles]: 'trashbin',
    [SafePromoType.afterMoveCleanerFiles]: 'clnr-files',
    [SafePromoType.afterMoveCleanerLetters]: 'clnr-mail',
    [SafePromoType.moreAboutSafe]: 'about',
    [SafePromoType.manage]: 'manage',
} as const;

export type VideoCoverImg = { webm: string; mp4: string };

export const safePromoDialogCoverConfig: {
    dark: { [k in SafePromoType]: string | VideoCoverImg };
    light: { [k in SafePromoType]: string | VideoCoverImg };
} = {
    dark: {
        [SafePromoType.base]: promoImgDark,
        [SafePromoType.afterMoveTrashbinFiles]: promoImgDark,
        [SafePromoType.afterMoveCleanerFiles]: { webm: safePromoCleanerCloudWebm, mp4: safePromoCleanerCloudMp4 },
        [SafePromoType.afterMoveCleanerLetters]: { webm: safePromoCleanerMailWebm, mp4: safePromoCleanerMailMp4 },
        [SafePromoType.moreAboutSafe]: promoImgDark,
        [SafePromoType.manage]: managePromoImgDark,
    },
    light: {
        [SafePromoType.base]: promoImg,
        [SafePromoType.afterMoveTrashbinFiles]: promoImg,
        [SafePromoType.afterMoveCleanerFiles]: { webm: safePromoCleanerCloudWebm, mp4: safePromoCleanerCloudMp4 },
        [SafePromoType.afterMoveCleanerLetters]: { webm: safePromoCleanerMailWebm, mp4: safePromoCleanerMailMp4 },
        [SafePromoType.moreAboutSafe]: promoImg,
        [SafePromoType.manage]: managePromoImg,
    },
};

export const isVideoCover = (promoType: SafePromoType) => {
    return typeof safePromoDialogCoverConfig.dark[promoType] === 'object';
};

export const sendSafeModalDwh = ({
    buttonName,
    action,
    promoType,
}: {
    action: 'show' | 'click' | 'close';
    buttonName?: 'ok' | 'more';
    groupId?: UserQuotaGroupId;
    promoType: SafePromoType;
}) => {
    const analyticPromoTypeName = AnalyticSafePromoTypeName[promoType] || 'none';
    const name = `safe-promo-dlg_${analyticPromoTypeName}`;

    const dwhData = {
        name,
        paid_user: IS_PAID_USER,
        platform: getPlatformInfo().join('_'),
        button_name: buttonName,
    };

    const optionalButtonField: (keyof typeof dwhData)[] = buttonName ? ['button_name'] : [];
    const dwh_enabled_fields: (keyof typeof dwhData)[][] = [['name', ...optionalButtonField, 'platform']];

    sendDwh({
        eventCategory: 'modal',
        action,
        dwh_enabled: true,
        dwh_enabled_fields,
        dwhData,
    });
};

export const preloadSafePromoDialogCover = async (promoType: SafePromoType) => {
    const isDarkTheme = isDarkThemeModeEnabled();
    const imageUrl =
        (isDarkTheme ? safePromoDialogCoverConfig.dark[promoType] : safePromoDialogCoverConfig.light[promoType]) ||
        safePromoDialogCoverConfig.light.base;

    if (isVideoCover(promoType)) {
        const { webm, mp4 } = imageUrl as VideoCoverImg;
        await preloadImage({ src: webm, timeout: IMAGE_PRELOAD_TIMEOUT });
        await preloadImage({ src: mp4, timeout: IMAGE_PRELOAD_TIMEOUT });
    } else {
        const src = imageUrl as string;
        await preloadImage({ src, timeout: IMAGE_PRELOAD_TIMEOUT });
    }
};
